import React, { useState, useEffect, useCallback } from 'react';
import { useUserSession } from '../contexts/UserContext.js';
import { usePendingOperations } from '../contexts/PendingOperationsContext.js';
import { useAuth0 } from '@auth0/auth0-react';
import { isDevelopment, getApiUrl } from '../utils/environment.js';
import FomoAlert from './FomoAlert.js';
import NotificationSetupModal from './NotificationSetupModal.js';
import './Settings.css';
import back from '../Ui/backWhite.png';

const Settings = ({ goBack }) => {
  const { userSession, updateUserSession } = useUserSession();
  const { trackOperation, requestNavigation, hasPendingOperations } = usePendingOperations();
  const { getAccessTokenSilently } = useAuth0();
  
  // Simple device detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const deviceType = isMobile ? 'Mobile' : 'Desktop';
  
  // Development bypass pattern for testing
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const allowDevBypass = isLocalhost && isDevelopment();
  
  // Check if user has access (paid and session loaded) OR development bypass
  const hasAccess = (userSession.isLoaded && userSession.hasPaid) || allowDevBypass;
  
  // FOMO alert state
  const [showFomoAlert, setShowFomoAlert] = useState(false);
  
  // Notification setup modal state
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  
  // Initialize from userSession (loaded from DB on login) - persists across navigation
  const [notificationSettings, setNotificationSettings] = useState({
    browserNotifications: userSession.notificationsAllowed || false,
    emailPhoneNotifications: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSaving, setIsSaving] = useState(false); // Prevent reload during save
  const [userChangedNotifications, setUserChangedNotifications] = useState(false); // Track if user made the change
  const [blockageDetection, setBlockageDetection] = useState({
    isBlocked: false,
    blockReason: null,
    canRequest: true,
    details: null
  });

  // Wrap goBack with navigation guard
  const handleGoBack = useCallback(() => {
    requestNavigation(() => goBack());
  }, [goBack, requestNavigation]);

  // Register Service Worker on component mount - NEEDED FOR ALL PLATFORMS
  useEffect(() => {
    let isMounted = true;
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          if (isMounted) {
            setServiceWorkerReady(true);
          }
        })
        .catch((error) => {
          if (isMounted) {
            setServiceWorkerReady(false);
          }
        });
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Sync notification settings with userSession (source of truth, loaded from DB on login)
  // This ensures Settings reflects the persisted value across navigation
  useEffect(() => {
    // Skip sync if save is in progress (we're updating the source)
    if (isSaving || hasPendingOperations) {
      return;
    }
    
    if (!userSession.isLoaded) {
      return;
    }
    
    // Sync local state with userSession value
    const dbValue = userSession.notificationsAllowed || false;
    setUserChangedNotifications(false); // Mark as NOT user-initiated
    setNotificationSettings(prev => ({
      ...prev,
      browserNotifications: dbValue
    }));
    
    setIsInitialLoad(false);
  }, [userSession.isLoaded, userSession.notificationsAllowed, isSaving, hasPendingOperations]);

  // Update notification preference in database
  const updateDatabasePreferences = useCallback(async () => {
    const previousValue = !notificationSettings.browserNotifications; // Store previous state for rollback
    
    setIsSaving(true); // Prevent reload during save
    
    // Track this operation so navigation is blocked until complete
    await trackOperation('save-notification-preference', async () => {
      try {
        const token = await getAccessTokenSilently();
        const cleanApiUrl = getApiUrl().replace(/\/$/, '');
        
        const response = await fetch(`${cleanApiUrl}/api/user/profile`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            notificationsAllowed: notificationSettings.browserNotifications
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to save preference (${response.status})`);
        }
        
        await response.json();
        // Success - update UserContext so it persists across navigation
        updateUserSession({ notificationsAllowed: notificationSettings.browserNotifications });
      } catch (error) {
        // Revert toggle to previous state on failure
        setNotificationSettings(prev => ({
          ...prev,
          browserNotifications: previousValue
        }));
        // Show user feedback
        alert('Failed to save notification preference. Please try again.');
      } finally {
        setIsSaving(false);
      }
    });
  }, [getAccessTokenSilently, notificationSettings.browserNotifications, trackOperation, updateUserSession]);

  // Update database when notification settings change (ONLY when user-initiated)
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }
    
    // CRITICAL: Only save if the user actually made the change
    if (!userChangedNotifications) {
      return;
    }
    
    if (userSession.isLoaded && userSession.uniqueId) {
      updateDatabasePreferences();
      // Reset the flag after save is triggered
      setUserChangedNotifications(false);
    }
  }, [notificationSettings.browserNotifications, isInitialLoad, userChangedNotifications, userSession.isLoaded, userSession.uniqueId, updateDatabasePreferences]);

  // Simplified blockage detection - only real blockers
  const detectNotificationBlockage = async () => {
    const detection = {
      isBlocked: false,
      blockReason: null,
      canRequest: true,
      details: null
    };

    // 1. Check if Notifications API exists
    if (!('Notification' in window)) {
      detection.isBlocked = true;
      detection.blockReason = 'API_NOT_SUPPORTED';
      detection.canRequest = false;
      detection.details = 'Browser does not support notifications';
      setBlockageDetection(detection);
      return detection;
    }

    // 2. Check for iframe context
    if (window !== window.top) {
      detection.isBlocked = true;
      detection.blockReason = 'IFRAME_CONTEXT';
      detection.canRequest = false;
      detection.details = 'Notifications cannot be requested from within an iframe';
      setBlockageDetection(detection);
      return detection;
    }

    // 3. Check for HTTPS requirement (except localhost)
    const isSecure = window.location.protocol === 'https:' || 
                    window.location.hostname === 'localhost' || 
                    window.location.hostname === '127.0.0.1';
    
    if (!isSecure) {
      detection.isBlocked = true;
      detection.blockReason = 'INSECURE_CONTEXT';
      detection.canRequest = false;
      detection.details = 'Notifications require HTTPS or localhost';
      setBlockageDetection(detection);
      return detection;
    }

    // 4. Only show denied state as blocked (not speculative checks)
    if (Notification.permission === 'denied') {
      detection.isBlocked = true;
      detection.blockReason = 'PERMISSION_DENIED';
      detection.canRequest = false;
      detection.details = 'Notifications blocked - copy chrome://settings/content/notifications to new tab to fix';
      setBlockageDetection(detection);
      return detection;
    }

    // All checks passed - notifications should work
    detection.isBlocked = false;
    detection.canRequest = true;
    setBlockageDetection(detection);
    return detection;
  };

  // Run detection on mount and when permission changes
  useEffect(() => {
    detectNotificationBlockage();
  }, []);


  // Check permission when page becomes visible (user might have changed browser settings)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        detectNotificationBlockage();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleBrowserNotificationToggle = async () => {
    if (!('Notification' in window)) {
      return;
    }

    // Turn off - simple toggle (user-initiated, will save to DB)
    if (notificationSettings.browserNotifications) {
      setUserChangedNotifications(true);
      setNotificationSettings(prev => ({ ...prev, browserNotifications: false }));
      return;
    }

    // Turn on - request permission first, then verify notification works
    Notification.requestPermission().then(async (permission) => {
      if (permission === 'granted') {
        // Send confirmation notification and check if it actually works
        const notificationWorked = await sendConfirmationNotification();
        
        if (notificationWorked) {
          // SUCCESS: Notification was shown - NOW enable toggle and save to DB
          setUserChangedNotifications(true);
          setNotificationSettings(prev => ({ ...prev, browserNotifications: true }));
        } else {
          // FAILED: Notification didn't show - keep toggle OFF, don't save to DB
          // Show help modal on desktop
          if (!isMobile) {
            setTimeout(() => {
              setShowNotificationModal(true);
            }, 500);
          }
        }
        
        detectNotificationBlockage();
      } else if (permission === 'denied') {
        setNotificationSettings(prev => ({ ...prev, browserNotifications: false }));
      }
    }).catch((error) => {
      // Permission request failed
    });
  };

  // Send confirmation notification after permission granted
  const sendConfirmationNotification = async () => {
    // CRITICAL: Verify browser permission before attempting to show notification
    if (Notification.permission !== 'granted') {
      return false;
    }

    const notificationOptions = {
      body: 'You will now receive important updates and progress notifications.',
      icon: window.location.origin + '/favicon.ico',
      badge: window.location.origin + '/favicon.ico',
      tag: 'intellegacy-enabled-' + Date.now(),
      requireInteraction: false,
      silent: false,
      renotify: true,
      vibrate: [200, 100, 200]
    };

    let notificationShown = false;

    try {
      // METHOD 1: Service Worker registration.showNotification() - Most reliable for HTTPS
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          
          if (registration) {
            const notificationPromise = registration.showNotification('🧠 Intellegacy Notifications Enabled', notificationOptions);
            await notificationPromise;
            
            // VERIFY: Check if notification actually exists
            const notifications = await registration.getNotifications();
            
            if (notifications.length > 0) {
              notificationShown = true;
              return true;
            } else {
              return false;
            }
          }
        } catch (swError) {
          // Service Worker method failed, try next method
        }
      }
      
      // METHOD 2: Direct Notification API - Works on HTTP and some browsers
      if (!notificationShown) {
        try {
          const notification = new Notification('🧠 Intellegacy Notifications Enabled', {
            body: notificationOptions.body,
            icon: notificationOptions.icon,
            tag: notificationOptions.tag,
            requireInteraction: notificationOptions.requireInteraction,
            silent: notificationOptions.silent,
            renotify: notificationOptions.renotify,
            vibrate: notificationOptions.vibrate
          });
          
          notification.onshow = () => {
            notificationShown = true;
          };
          
          // If notification was created successfully, assume it will show
          notificationShown = true;
          return true;
        } catch (directError) {
          // Direct Notification API failed
        }
      }
      
      // METHOD 3: Fallback - Try to focus window and retry direct API
      if (!notificationShown) {
        try {
          window.focus();
          
          setTimeout(() => {
            const notification = new Notification('🧠 Intellegacy Notifications Enabled', {
              body: notificationOptions.body,
              icon: notificationOptions.icon,
              tag: notificationOptions.tag + '-retry'
            });
            
            notificationShown = true;
          }, 100);
          
          return;
        } catch (retryError) {
          // Focus retry failed
        }
      }
      
      // METHOD 4: Last resort - postMessage to Service Worker
      if (!notificationShown && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          
          if (registration.active) {
            registration.active.postMessage({
              type: 'SHOW_NOTIFICATION',
              title: '🧠 Intellegacy Notifications Enabled',
              options: notificationOptions
            });
            notificationShown = true;
            return;
          }
        } catch (postError) {
          // postMessage method failed
        }
      }
      
    } catch (error) {
      // All notification methods failed
    }
    
    if (!notificationShown) {
      return false;
    }
    
    return true;
  };

  // Retry notification setup function for the modal
  const retryNotificationSetup = () => {
    handleBrowserNotificationToggle();
  };

  // Handle modal close - if user clicks "I'll do this later", turn notifications back off
  const handleModalClose = () => {
    setUserChangedNotifications(true);
    setNotificationSettings(prev => ({ ...prev, browserNotifications: false }));
    setShowNotificationModal(false);
  };

  const handleEmailPhoneNotificationToggle = () => {
    // Don't allow disabling email/phone notifications
    if (notificationSettings.emailPhoneNotifications) {
      alert('Sorry, you can\'t disable email/phone notifications right now. These are currently required to keep the elite user-base updated on important account information.');
      return; // Don't actually toggle the setting
    }
    
    // This shouldn't happen since they start enabled, but just in case
    setNotificationSettings(prev => ({
      ...prev,
      emailPhoneNotifications: true
    }));
  };

  const testNotification = async () => {
    if (!('Notification' in window) || !notificationSettings.browserNotifications) {
      return;
    }

    // CRITICAL: Check actual browser permission, not just DB state
    if (Notification.permission !== 'granted') {
      alert('Browser notification permission not granted. Please enable notifications first.');
      return;
    }

    const notificationOptions = {
      body: 'This is a test notification from Intellegacy. If you can see this, notifications are working perfectly!',
      icon: window.location.origin + '/favicon.ico',
      badge: window.location.origin + '/favicon.ico',
      tag: 'intellegacy-test-' + Date.now(),
      requireInteraction: false,
      silent: false,
      renotify: true,
      vibrate: [200, 100, 200]
    };

    let notificationShown = false;

    const showNotification = async () => {
      try {
        // METHOD 1: Service Worker registration.showNotification() - Most reliable
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            
            if (registration) {
              await registration.showNotification('🧠 Intellegacy Test Notification', notificationOptions);
              
              // VERIFY: Check if notification actually exists after a brief delay
              setTimeout(async () => {
                const notifications = await registration.getNotifications();
                
                if (notifications.length > 0) {
                  notificationShown = true;
                } else {
                  if (!isMobile) {
                    setShowNotificationModal(true);
                  }
                }
              }, 500);
              
              return;
            }
          } catch (swError) {
            // Service Worker method failed
          }
        }
        
        // METHOD 2: Direct Notification API
        if (!notificationShown) {
          try {
            const notification = new Notification('🧠 Intellegacy Test Notification', {
              body: notificationOptions.body,
              icon: notificationOptions.icon,
              tag: notificationOptions.tag,
              requireInteraction: notificationOptions.requireInteraction,
              silent: notificationOptions.silent,
              renotify: notificationOptions.renotify,
              vibrate: notificationOptions.vibrate
            });
            
            notification.onshow = () => {
              notificationShown = true;
            };
            
            notification.onerror = () => {
              if (!isMobile) {
                setShowNotificationModal(true);
              }
            };
            
            notificationShown = true;
            return;
          } catch (directError) {
            if (!isMobile) {
              setShowNotificationModal(true);
            }
          }
        }
        
        // METHOD 3: Focus window and retry
        if (!notificationShown) {
          try {
            window.focus();
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            new Notification('🧠 Intellegacy Test Notification', {
              body: notificationOptions.body,
              icon: notificationOptions.icon,
              tag: notificationOptions.tag + '-retry'
            });
            
            notificationShown = true;
            return;
          } catch (retryError) {
            // Focus retry failed
          }
        }
        
        // METHOD 4: postMessage to Service Worker
        if (!notificationShown && 'serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            
            if (registration.active) {
              registration.active.postMessage({
                type: 'SHOW_NOTIFICATION',
                title: '🧠 Intellegacy Test Notification',
                options: notificationOptions
              });
              notificationShown = true;
              return;
            }
          } catch (postError) {
            // postMessage method failed
          }
        }
        
      } catch (error) {
        if (!isMobile) {
          setShowNotificationModal(true);
        }
      }
      
      if (!notificationShown) {
        if (!isMobile) {
          setTimeout(() => {
            setShowNotificationModal(true);
          }, 1000);
        }
      }
    };

    // Delay before showing notification
    setTimeout(() => {
      showNotification();
    }, 500);
  };

  const getPermissionStatusText = () => {
    if (!('Notification' in window)) {
      return 'Not supported';
    }
    
    return notificationSettings.browserNotifications ? 'Enabled' : 'Disabled';
  };

  const getPermissionStatusClass = () => {
    if (!('Notification' in window)) {
      return 'permission-default';
    }
    
    return notificationSettings.browserNotifications ? 'permission-granted' : 'permission-default';
  };

  // Show loading state while session is loading
  if (!userSession.isLoaded) {
    return (
      <div className="settings-container">
        <div className="settings-loading">Loading...</div>
      </div>
    );
  }

  // Check access control - show FOMO preview for unpaid users
  if (!hasAccess) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button className="back-button" onClick={handleGoBack}>
            <img src={back} className="back-btn-arrow"></img> Back to Dashboard
          </button>
          <h1>Settings</h1>
        </div>
        
        {/* Show disabled preview of settings */}
        <div className="settings-content disabled-preview">
          <div className="settings-section">
            <h3>Notification Preferences</h3>
            <div className="setting-item">
              <label className="setting-label disabled">
                <input 
                  type="checkbox" 
                  disabled 
                  className="disabled"
                />
                🔒 Browser Notifications (Premium)
              </label>
              <button 
                className="test-notification-btn disabled"
                onClick={() => setShowFomoAlert(true)}
              >
                🔒 Test
              </button>
            </div>
            
            <div className="setting-item">
              <label className="setting-label disabled">
                <input 
                  type="checkbox" 
                  disabled 
                  className="disabled"
                />
                🔒 Phone Notifications (Premium)
              </label>
            </div>
            
            <button 
              className="enable-notifications-btn disabled"
              onClick={() => setShowFomoAlert(true)}
            >
              🔒 Enable All Notifications (Premium)
            </button>
          </div>

          <div className="settings-section">
            <h3>⚙️ Advanced Settings</h3>
            <p className="preview-text">More premium settings available...</p>
            <button 
              className="preview-unlock-btn"
              onClick={() => setShowFomoAlert(true)}
            >
              🔓 Unlock All Settings
            </button>
          </div>
        </div>

        <FomoAlert 
          isOpen={showFomoAlert}
          onClose={() => setShowFomoAlert(false)}
          featureName="Settings & Notifications"
          customMessage="Probably want to complete registration first, then maybe you'll be allowed to customize your notification preferences and access advanced settings. Just saying... 🤷‍♂️"
        />
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
          <button className="back-button" onClick={handleGoBack}>
            <img src={back} className="back-btn-arrow"></img> Back to Dashboard
          </button>
        <h1>Settings</h1>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2 className='TopHeader'>🤖 Notification Preferences</h2>
          <p className="settings-description">
            Stay informed about your digital consciousness progress and important updates.
          </p>

          <div className="setting-item">
            <div className="setting-info">
              <h3>Browser Notifications ({deviceType})</h3>
              <p>Receive real-time updates and alerts in your browser.</p>
              <div className={`permission-status ${getPermissionStatusClass()}`}>
                Browser Permission: {getPermissionStatusText()}
              </div>
              
              {/* Blockage Detection Alert */}
              {blockageDetection.isBlocked && (
                <div className="blockage-alert" style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#856404'
                }}>
                  <strong>⚠️ Issue Detected:</strong> {blockageDetection.details}
                  {blockageDetection.blockReason === 'PERMISSION_DENIED' && (
                    <div style={{ marginTop: '4px', fontSize: '12px' }}>
                      Copy <code>chrome://settings/content/notifications</code> to a new tab to fix
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="setting-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={notificationSettings.browserNotifications}
                  onChange={handleBrowserNotificationToggle}
                  disabled={isLoading || (blockageDetection.isBlocked && !blockageDetection.canRequest)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <h3>Email/Phone Notifications</h3>
              <p>Receive important updates via email and phone. Required as per terms and conditions.</p>
              <div className="feature-status">
                Always Enabled - Required for important account updates
              </div>
            </div>
            <div className="setting-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={notificationSettings.emailPhoneNotifications}
                  onChange={handleEmailPhoneNotificationToggle}
                  disabled={true}
                />
                <span className="toggle-slider disabled"></span>
              </label>
            </div>
          </div>

          {notificationSettings.browserNotifications && (
            <div className="test-section">
              <button className="test-button" onClick={testNotification}>
                Send Test Notification
              </button>
            </div>
          )}
        </div>

        <div className="settings-info">
          <h3>👾 About Notifications</h3>
          <ul>
            <li><strong>Training Progress:</strong> Get notified when your AI training reaches milestones</li>
            <li><strong>System Updates:</strong> Important announcements and feature releases</li>
            <li><strong>Security Alerts:</strong> Account security and login notifications</li>
            <li><strong>Referral Rewards:</strong> Notifications when you earn legacy coins</li>
          </ul>
          
          <div className="privacy-note">
            <h4 className='PrivacyControlHeader'>👽 Privacy & Control</h4>
            <p>
              You can change these settings anytime. We respect your privacy and only send 
              notifications that are relevant to your Intellegacy experience. You can also 
              manage browser notification permissions directly in your browser settings.
            </p>
          </div>
        </div>
      </div>
      
      <NotificationSetupModal 
        isOpen={showNotificationModal}
        onClose={handleModalClose}
        onRetry={retryNotificationSetup}
      />
      
      <FomoAlert 
        isOpen={showFomoAlert}
        onClose={() => setShowFomoAlert(false)}
        featureName="Settings & Notifications"
        customMessage="Probably want to complete registration first, then maybe you'll be allowed to customize your notification preferences and access advanced settings. Just saying... 🤷‍♂️"
      />
    </div>
  );
};

export default Settings;
